import { WindowLoader } from '@/components/PageLoader';
import { useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { apiFetch, configureApiClient } from '@/api/client';
import { useShortcuts } from '@/components/shortcuts';
import { useNotify } from '@/api/notify';
import { isUnreachable } from '@/api/problem';
import { setCompanyDateFormat } from '@core/format/datePreference';
import { useDateFormat } from '@/format/useDateFormat';
import { ChangePasswordPage } from '@/pages/ChangePasswordPage';
import { ComingSoonPage } from '@/pages/ComingSoonPage';
import { MaintenancePage } from '@/pages/MaintenancePage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { SignInPage } from '@/pages/SignInPage';
import { TwoFactorPage } from '@/pages/TwoFactorPage';
import { isBuilt } from '@/pages/builtKeys';
import { pages } from '@/pages/registry';
import {
  clearTenantEndpoint,
  loadTenantEndpoint,
  saveTenantEndpoint,
  type TenantEndpoint,
} from '@/session/tenantEndpoint';
import type { AccessibleBranch, Session, SignedInUser } from '@core/session/types';
import { AppShell } from '@/shell/AppShell';
import { isMisdirected, landingKey } from '@/shell/landing';
import { HOME_KEY, keyForPath, pathForKey } from '@/shell/location';
import { findMenuDestination } from '@/shell/menu';
import { setShopPaper } from '@/pages/print/paper';
import { setCompanyAccent } from '@/shell/accent';
import { setCompanyTextScale } from '@/shell/textScale';

/** What `POST /api/auth/refresh` answers — the same shape sign-in does, minus the fields unused here. */
interface RefreshResponse {
  accessToken: string;
  user: SignedInUser;
}

/**
 * Shell preview for milestones 1.7.5–1.7.7, built ahead of 1.7.3/1.7.4 so the visual direction can
 * be judged before three milestones inherit it.
 *
 * The session is held in local state here rather than in a provider — extracting one is still
 * 1.7.4's job. Silent refresh on mount landed ahead of that: it is a self-contained effect rather
 * than a reason to bring the provider forward. Everything else is real: the sign-in, the
 * permissions, the entitlements and the branch list all come from `GET /api/auth/me`, so the menu
 * you see is the menu that user actually gets.
 */
export function App() {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [user, setUser] = useState<SignedInUser | null>(null);
  const [branch, setBranch] = useState<AccessibleBranch | undefined>(undefined);
  // Seeded from the URL, not from a constant: this state *is* the current screen, and starting it
  // at `branch` on every load is what sent a reload of `/bookings` to the branches list.
  const [activeKey, setActiveKey] = useState(() => keyForPath(window.location.pathname));
  // The saved view a navigation asked for, consumed by `screenFor` and cleared by the next move.
  const [pendingView, setPendingView] = useState<string | undefined>(undefined);
  // The code the global find resolved, travelling the same way and cleared by the same move.
  const [pendingFind, setPendingFind] = useState<string | undefined>(undefined);
  // The one record the find settled on — a scanned booking slip — opened rather than listed.
  const [pendingRecord, setPendingRecord] = useState<number | undefined>(undefined);
  const [changingPassword, setChangingPassword] = useState(false);
  const [managingTwoFactor, setManagingTwoFactor] = useState(false);
  const notify = useNotify();
  const queryClient = useQueryClient();

  // Alt+S, Alt+P, Alt+N, Alt+B — one listener for the whole app, because the point of a shortcut is
  // that it works while focus is still in the last field. Installed here rather than per screen so
  // the sign-in screens get it too, where it resolves to nothing and costs nothing.
  useShortcuts();

  /*
    The date format is read by `format/dates.ts`, which is pure functions rather than hooks, so
    nothing below this point subscribes to it. This one call is what makes a change on the settings
    screen or in the account menu repaint the tree instead of waiting for the next reload — the same
    job `useThemeMode()` does for Ant Design's palette in `AppProviders`.
  */
  useDateFormat();

  // Read from storage once, on mount. Re-reading per render would let a value written in another
  // tab change which API this one is talking to mid-session, which is not a thing a signed-in
  // session should survive silently.
  const [tenant, setTenant] = useState<TenantEndpoint | null>(() => loadTenantEndpoint());

  // True until the mount-time silent refresh below has had its answer. The session itself lives
  // only in this component's state, so without this a page reload has nothing in memory yet and
  // renders the sign-in screen for the instant it takes to find out there was no need to.
  const [restoringSession, setRestoringSession] = useState(true);

  // Set when that attempt found nothing listening — see the note on the catch below.
  const [apiUnreachable, setApiUnreachable] = useState(false);

  configureApiClient({
    getAccessToken: () => accessToken,
    getBranchId: () => branch?.id ?? null,

    // Every call after the company step goes to that company's API. Null — the normal case — keeps
    // the relative paths that let the dev proxy and a same-origin deployment work untouched.
    getApiBaseUrl: () => tenant?.apiBaseUrl ?? null,

    // An access token dies mid-session far more often than a user notices — it is short-lived by
    // design. Without this the call that hit the 401 fails and nothing else happens: the shell
    // keeps rendering against a session that is already gone until something else stumbles on it.
    onUnauthenticated: () => {
      clearSession();
      notify.info('Your session has expired. Sign in again.');
    },
  });

  /*
    The refresh token is an HttpOnly cookie the browser already holds; this component's state is
    not. A reload starts that state at null every time, which — without this — reads as "signed
    out" even though the server would still honour the session. So on mount, and only if a company
    is already known (no cookie can exist for one that isn't), trade the cookie for a fresh access
    token before deciding whether to show the sign-in screen at all.

    This must happen exactly once per load, and the ref is what guarantees it. Refreshing *rotates*
    the token, so a second exchange presents one the first has already retired — which
    `AuthService.RefreshAsync` cannot distinguish from a stolen token and answers by revoking the
    user's entire token family, successor included. StrictMode runs mount effects twice in
    development, so without the guard the second run either races the first or replays it outright,
    and the failure is a full sign-out on a page that was only being reloaded. A ref survives
    StrictMode's simulated remount, which a local variable would not.

    Deliberately no cancellation flag. StrictMode's cleanup is indistinguishable from a real
    unmount, so a flag set there would suppress the *first* run's state updates and leave the
    spinner up forever. Resolving after a genuine unmount is a harmless no-op in React 18.
  */
  const restoreAttempted = useRef(false);

  useEffect(() => {
    if (restoreAttempted.current) {
      return;
    }

    restoreAttempted.current = true;

    if (tenant === null) {
      setRestoringSession(false);
      return;
    }

    void apiFetch<RefreshResponse>('/api/auth/refresh', { method: 'POST', anonymous: true })
      .then((auth) => {
        setAccessToken(auth.accessToken);
        setUser(auth.user);
        setBranch(auth.user.branches[0]);
      })
      .catch((caught: unknown) => {
        /*
          Two failures arrive here and only one of them is ordinary.

          A 401 is the common case and not an error at all: no cookie, or the server declined it,
          which is what being signed out looks like. Falling through to the sign-in screen is the
          correct answer and saying anything would be noise.

          Nothing answering is the other. The sign-in screen is useless against an API that is not
          there — every credential typed into it fails on submit with no explanation of why — so
          this is the one point in the app where a failed request is worth taking the whole screen
          for. It is deliberately limited to startup; see `MaintenancePage`.
        */
        if (isUnreachable(caught)) {
          setApiUnreachable(true);
        }
      })
      .finally(() => setRestoringSession(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * The single place a resolved company is recorded — storage and state together, so the screen,
   * the API client and the next visit can never hold three different answers.
   *
   * Stable identity: the sign-in screen re-resolves a remembered company from an effect, and a
   * callback that changed every render would abort that request and never retry it.
   */
  const rememberTenant = useCallback((next: TenantEndpoint) => {
    saveTenantEndpoint(next);
    setTenant(next);
  }, []);

  /** "Change company" — forget it and go back to step one. Stable, for the same reason. */
  const forgetTenant = useCallback(() => {
    clearTenantEndpoint();
    setTenant(null);
  }, []);

  /**
   * The one place navigation happens — the key the shell renders by, and the URL, moved together.
   *
   * `pushState` rather than a load: the session lives in this component's state and following the
   * sidebar's real `href` would drop it, which is why `Sidebar` calls `preventDefault` on every row.
   * A key with no path of its own — a group heading — moves the screen and leaves the URL alone
   * rather than inventing an address for something that has none.
   *
   * Guarded on the path actually differing, or re-clicking the current row stacks identical history
   * entries and the back button appears to do nothing several times over.
   */
  const navigate = useCallback((key: string, view?: string, find?: string, record?: number) => {
    setActiveKey(key);

    /*
      The saved view travels beside the key rather than in the URL, which is the same rule every
      other sub-screen in this shell follows — putting it in the address needs route parameters.

      Held in its own state and read by `screenFor` for exactly one render's worth of navigation;
      `popstate` below clears it, so going back does not re-apply a view the previous entry never
      had. Without this a Today card would land the clerk on the unfiltered register, which is the
      clicking that screen exists to remove.
    */
    setPendingView(view);
    setPendingFind(find);
    setPendingRecord(record);

    const path = pathForKey(key);

    if (path !== null && path !== window.location.pathname) {
      window.history.pushState(null, '', path);
    }
  }, []);

  /*
    The company's choice, pushed into the store whenever the session changes — including on sign-out,
    where `user` is null and the store must fall back rather than leave the last company's format in
    place for whoever signs in next on this till.

    In an effect rather than during render, because writing to an external store and notifying its
    listeners mid-render is how React ends up updating a component while rendering another. It
    settles one render later, which is invisible: `datePreference` caches the company's format, so on
    every load after the first the very first paint is already right and this only confirms it.
  */
  useEffect(() => {
    setCompanyDateFormat(user?.dateFormat ?? null);
  }, [user?.dateFormat]);

  /*
    The company's text scale, on the same terms and for the same reasons as the format above —
    including the sign-out case, where leaving the last company's size in place would greet whoever
    signs in next on this till at a scale their own company never chose.

    `setCompanyTextScale` writes the CSS custom properties as well as notifying subscribers, so the
    template half and the Ant Design half move together. `textScale.ts` applies the cached answer at
    module load, so this confirms the first paint rather than correcting it.
  */
  useEffect(() => {
    setCompanyTextScale(user?.textScale ?? null);
  }, [user?.textScale]);

  /*
    The company's brand accent, on exactly the same terms as the two above — including the sign-out
    case, where leaving the last company's colour up would greet whoever signs in next on this till
    in a brand that is not theirs.

    Its own effect rather than sharing the scale's: two settings that happen to arrive together are
    still two settings, and one dependency array covering both would re-apply an unchanged colour
    every time somebody's company changed its type size.
  */
  useEffect(() => {
    setCompanyAccent(user?.accent ?? null);
  }, [user?.accent]);

  // The shop's starting paper for every print, on the same terms as the three above.
  useEffect(() => {
    setShopPaper(user?.printPaper ?? null);
  }, [user?.printPaper]);

  /*
    Somebody who may not see the screen they landed on is moved to one they can.

    `HOME_KEY` is a constant and cannot know who signed in, so a user without `Branch:View` was sent
    to the branches screen and answered with a permission error by the first thing the product drew
    for them — a screen the sidebar had correctly refused to offer.

    A redirect rather than `screenFor` picking a different screen, because the active key is what
    the sidebar highlights: rendering other content under the old key would light up a row that does
    not match what is on screen. And only for a key the manifest *declares* — a mistyped address
    keeps its 404, which `location.ts` argues for at length.

    `user` and `branch` rather than `session`, which is assembled below the early returns and so
    cannot be a dependency of a hook.
  */
  useEffect(() => {
    if (!user) {
      return;
    }

    const session: Session = { user, branch };

    if (!isMisdirected(session, activeKey)) {
      return;
    }

    const destination = landingKey(session);

    // Null means they can reach nothing at all. Leaving the key alone lets the 404 say so, rather
    // than looping them back into the screen that just refused them.
    if (destination !== null) {
      navigate(destination);
    }
  }, [user, branch, activeKey, navigate]);

  // Back and forward move the browser's history without telling React, so without this the URL
  // changes and the screen under it does not.
  useEffect(() => {
    const onPopState = () => {
      setActiveKey(keyForPath(window.location.pathname));
      // The view was never in the address, so a history entry cannot carry one.
      setPendingView(undefined);
      setPendingFind(undefined);
      setPendingRecord(undefined);
    };

    window.addEventListener('popstate', onPopState);

    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  /**
   * Drops the session locally. Split out because three different endings share it — signing out,
   * abandoning a forced password change, and completing one — and the last two are not sign-outs
   * in any sense the user would recognise, so they must not silently skip any of the clearing.
   *
   * The company survives all three. It is not a credential and not part of the session: whoever
   * signs in next on this till is overwhelmingly the same shop, and forgetting it would send them
   * back through the company step for no reason.
   *
   * Says nothing itself. Why the session ended differs at each of the three call sites, and only
   * two of them have anything worth telling the user — so the toast is raised by whichever one
   * knows, rather than by a parameter here that two callers would pass as undefined.
   *
   * The query cache goes with the session. It is keyed by URL, not by company or user, so whoever
   * signs in next on this till would otherwise be shown the last person's lists — another
   * company's, even — until each went stale. Clearing also cancels anything still in flight, so a
   * late answer can't put the old data back.
   */
  const clearSession = () => {
    queryClient.clear();
    setAccessToken(null);
    setUser(null);
    setBranch(undefined);
    setChangingPassword(false);
    setManagingTwoFactor(false);
  };

  const signOut = () => {
    // Clears the refresh cookie server-side; the access token simply goes out of scope. Fired at
    // whichever API the client currently points at, so the company must still be set when it runs.
    void apiFetch('/api/auth/logout', { method: 'POST' }).catch(() => undefined);

    clearSession();
  };

  if (restoringSession) {
    return <SessionRestoring />;
  }

  if (apiUnreachable) {
    return <MaintenancePage />;
  }

  if (!user) {
    return (
      <SignInPage
        tenant={tenant}
        onTenantResolved={rememberTenant}
        onChangeTenant={forgetTenant}
        onSignedIn={(auth) => {
          setAccessToken(auth.accessToken);
          setUser(auth.user);
          setBranch(auth.user.branches[0]);
        }}
      />
    );
  }

  const session: Session = { user, branch };

  /*
    An account on a one-time password never reaches the shell.

    This is not a nicety. `MustChangePassword` makes the API's permission handler refuse every
    permission-protected endpoint with `Auth_PasswordChangeRequired`, so rendering the shell for
    such a user produces an app in which every screen answers 403 and nothing explains why. The
    flag is a state with exactly two exits, and both of them are on this screen.
  */
  if (user.mustChangePassword || changingPassword) {
    return (
      <ChangePasswordPage
        user={user}
        forced={user.mustChangePassword}
        // The server ends every session on a password change, this one included, and the access
        // token still in hand carries the `MustChangePassword` claim baked in at issue time —
        // it would keep being refused even though the condition is gone. So the only honest thing
        // the client can do is start again, and say so.
        onChanged={() => {
          clearSession();
          notify.success('Your password has been changed. Sign in with your new password.');
        }}
        onCancel={() => (user.mustChangePassword ? signOut() : setChangingPassword(false))}
      />
    );
  }

  /*
    Two-factor setup, outside the shell for the same reason the password screen is: it is not a
    menu destination. The menu is a projection of permissions and this is not permissioned — a user
    with no grants at all still has a sign-in worth protecting.

    Closing it refreshes nothing on its own, so the `twoFactorEnabled` the shell's account menu
    renders comes from the session as it was at sign-in. That is corrected by re-reading the user
    when the screen closes — cheap, and it means the tag never contradicts what the user just did.
  */
  if (managingTwoFactor) {
    return (
      <TwoFactorPage
        user={user}
        onClose={() => {
          setManagingTwoFactor(false);

          void apiFetch<SignedInUser>('/api/auth/me')
            .then(setUser)
            .catch(() => undefined);
        }}
      />
    );
  }

  return (
    <AppShell
      session={session}
      activeKey={activeKey}
      onNavigate={navigate}
      onBranchChange={setBranch}
      onSignOut={signOut}
      onChangePassword={() => setChangingPassword(true)}
      onSecurity={() => setManagingTwoFactor(true)}
    >
      {screenFor(activeKey, session, navigate, pendingView, pendingFind, pendingRecord)}
    </AppShell>
  );
}

/**
 * The active key, resolved to something on screen — in one of three ways.
 *
 * A built key gets its screen. A key the menu manifest declares but nothing answers yet gets the
 * coming-soon screen, named from the manifest. A key nothing has heard of gets a 404, because the
 * alternative is an empty content column that looks identical to a screen that rendered nothing.
 *
 * The sidebar can only ever raise the first branch — it refuses clicks on pending rows, and every
 * key it can raise comes from the manifest. The other two are what the URL can produce, and now
 * routinely does: a typed or bookmarked address is not filtered by anything.
 */
function screenFor(
  activeKey: string,
  session: Session,
  onNavigate: (key: string, view?: string, find?: string, record?: number) => void,
  pendingView: string | undefined,
  pendingFind: string | undefined,
  pendingRecord: number | undefined,
): ReactNode {
  /*
    A built screen this person may not see is refused here as well as redirected above, and the
    belt is doing real work rather than doubling the braces.

    `isBuilt` asks whether a screen exists, never whether it is theirs — so without this, arriving
    on a key they lack renders the screen and fires its queries. Somebody holding no role at all was
    shown the full items list that way. Found by signing in as that user; every test was green.

    Nothing rather than a message, because the effect above always has somewhere to send them: the
    Today screen carries no `requires`, so `landingKey` cannot come back empty and the redirect
    lands on the next tick. A flash of "not found" on the way to a working screen reads as a fault.
    The honest "no role has been assigned to you" now lives on that screen, where the person can
    actually be left sitting on it.
  */
  if (isMisdirected(session, activeKey)) {
    return null;
  }

  if (isBuilt(activeKey)) {
    return pages[activeKey](session, {
      view: pendingView,
      find: pendingFind,
      record: pendingRecord,
      open: onNavigate,
    });
  }

  const destination = findMenuDestination(activeKey);

  if (destination) {
    return (
      <ComingSoonPage
        label={destination.label}
        {...(destination.group ? { group: destination.group } : {})}
      />
    );
  }

  /*
    "Back where you started" rather than a home screen — there is still no dashboard for it to mean
    instead. It is the first screen this person can actually use, falling back to the constant only
    when they can reach nothing: sending them to `branch` unconditionally would land somebody
    without `Branch:View` on a permission error, and the misdirection effect above would then have
    to bounce them off it, spending a history entry to correct a destination we already knew.
  */
  return <NotFoundPage onHome={() => onNavigate(landingKey(session) ?? HOME_KEY)} />;
}

/** What a reload shows while the silent-refresh attempt above is still in flight. */
function SessionRestoring() {
  return <WindowLoader />;
}
